import { getEl, getEls, setHTML } from './helpers.js'
import { setTheme } from './preferences.js'

const fallbackLanguageOptions = [
	{ label: 'Italiano', value: 'it', url: '/it/' },
	{ label: 'English', value: 'en', url: '/' },
]

function getLanguageOptions() {
	const options = window?.oblivioLanguages
	if (!Array.isArray(options) || !options.length) {
		return fallbackLanguageOptions
	}

	const normalizedOptions = options
		.map((option) => ({
			label: option?.label || option?.name || '',
			value: option?.value || option?.slug || '',
			url: option?.url || '',
		}))
		.filter((option) => option.label && option.value)

	if (!normalizedOptions.length) {
		return fallbackLanguageOptions
	}

	return normalizedOptions
}

const popupConfigs = {
	theme: {
		options: [
			{
				label: getThemeLabel('system', 'System'),
				value: 'system',
				img: 'system-mode.svg',
			},
			{
				label: getThemeLabel('dark', 'Dark'),
				value: 'dark',
				img: 'dark-mode.svg',
			},
			{
				label: getThemeLabel('light', 'Light'),
				value: 'light',
				img: 'light-mode.svg',
			},
		],
	},
	language: {
		options: getLanguageOptions(),
	},
}

function getThemeLabel(key, fallback) {
	const labels = window?.oblivioConfig?.themeLabels || {}
	const label = labels[key] || ''
	return label || fallback
}

const popupSelectors = {
	popup: '[data-settings-popup]',
	content: '[data-settings-content]',
	trigger: '[data-settings-trigger]',
}

const popupLayout = {
	offset: 8,
	edgePadding: 8,
	minWidth: 160,
	switchDelayMs: 160,
}

export function getPopupElements() {
	const popupElement = getEl(popupSelectors.popup)
	const popupScope = popupElement || document
	const popupContent = getEl(popupSelectors.content, popupScope)
	const triggerElements = getEls(popupSelectors.trigger)
	return { popupElement, popupContent, triggerElements }
}

export function hasPopupElements({
	popupElement,
	popupContent,
	triggerElements,
}) {
	const hasElement = hasPopupElement(popupElement)
	const hasContent = hasPopupContent(popupContent)
	const hasTriggers = hasTriggerElements(triggerElements)
	const hasAllElements = hasElement && hasContent && hasTriggers
	return hasAllElements
}

function hasPopupElement(popupElement) {
	return Boolean(popupElement)
}

function hasPopupContent(popupContent) {
	return Boolean(popupContent)
}

function hasTriggerElements(triggerElements) {
	return Boolean(triggerElements.length)
}

export function registerTriggerListeners({
	triggerElements,
	popupElement,
	popupContent,
	activeTrigger,
	switchTimeout,
}) {
	const triggerContext = {
		popupElement,
		popupContent,
		activeTrigger,
		switchTimeout,
		switchDelayMs: popupLayout.switchDelayMs,
	}
	const onTriggerClickHandler = onTriggerClick.bind(null, triggerContext)

	triggerElements.forEach((triggerElement) => {
		triggerElement.addEventListener('click', (event) =>
			onTriggerClickHandler(event, triggerElement)
		)
	})
	const didRegisterListeners = true
	return didRegisterListeners
}

export function registerPopupOptionListener({ popupElement, activeTrigger }) {
	const optionContext = { popupElement, activeTrigger }
	const onPopupOptionClickHandler = onPopupOptionClick.bind(
		null,
		optionContext
	)
	popupElement.addEventListener('click', onPopupOptionClickHandler)
	const didRegisterListener = true
	return didRegisterListener
}

export function registerOutsideClickListener({
	popupElement,
	triggerElements,
	activeTrigger,
}) {
	const outsideClickContext = {
		popupElement,
		triggerElements,
		activeTrigger,
	}
	const onOutsideClickHandler = onOutsideClick.bind(null, outsideClickContext)
	document.addEventListener('click', onOutsideClickHandler)
	const didRegisterListener = true
	return didRegisterListener
}

export function registerResizeListener({ popupElement, activeTrigger }) {
	const resizeContext = { popupElement, activeTrigger }
	const onResizeHandler = onResize.bind(null, resizeContext)
	window.addEventListener('resize', onResizeHandler)
	const didRegisterListener = true
	return didRegisterListener
}

export function setInitialPopupState(popupElement) {
	return setPopupInert(popupElement, true)
}

function handleOptionClick(target, popupElement, activeTrigger) {
	const optionButton = target?.closest?.('.settings-popup__option')
	const isOption = isOptionButton(optionButton)
	if (!isOption) return isOption

	const { optionType, optionValue, optionUrl } =
		getOptionPayload(optionButton)
	applyOptionSelection(optionType, optionValue, optionUrl)
	const didClosePopup = closePopup(popupElement, activeTrigger)
	const didHandleOption = isOption && didClosePopup
	return didHandleOption
}

function handleOutsideClick(
	event,
	popupElement,
	triggerElements,
	activeTrigger
) {
	const isVisible = isPopupVisible(popupElement)
	if (!isVisible) return isVisible

	const target = event.target
	const shouldClose = shouldCloseOnOutsideClick(
		popupElement,
		triggerElements,
		target
	)

	if (shouldClose) {
		const didClose = closePopup(popupElement, activeTrigger)
		return didClose
	}

	return shouldClose
}

function handleTriggerClick({
	event,
	popupElement,
	popupContent,
	triggerElement,
	activeTrigger,
	switchDelayMs,
	switchTimeout,
}) {
	event.preventDefault()

	clearSwitchTimeout(switchTimeout)

	const shouldToggle = shouldToggleActiveTrigger(
		activeTrigger,
		triggerElement,
		popupElement
	)

	if (shouldToggle) {
		const didClose = closePopup(popupElement, activeTrigger.current)
		activeTrigger.current = null
		return didClose
	}

	const shouldSwitch = shouldSwitchTrigger(activeTrigger, triggerElement)

	if (shouldSwitch) {
		closePopup(popupElement, activeTrigger.current)
		const nextTrigger = triggerElement
		const didScheduleSwitch = schedulePopupSwitch({
			popupElement,
			popupContent,
			nextTrigger,
			activeTrigger,
			switchTimeout,
			switchDelayMs,
		})
		return didScheduleSwitch
	}

	activeTrigger.current = triggerElement
	return openPopup(popupElement, popupContent, triggerElement)
}

function onTriggerClick(context, event, triggerElement) {
	return handleTriggerClick({
		event,
		popupElement: context.popupElement,
		popupContent: context.popupContent,
		triggerElement,
		activeTrigger: context.activeTrigger,
		switchDelayMs: context.switchDelayMs,
		switchTimeout: context.switchTimeout,
	})
}

function onPopupOptionClick(context, event) {
	const didHandle = handleOptionClick(
		event.target,
		context.popupElement,
		context.activeTrigger.current
	)

	if (didHandle) {
		context.activeTrigger.current = null
	}

	return didHandle
}

function onOutsideClick(context, event) {
	const didClose = handleOutsideClick(
		event,
		context.popupElement,
		context.triggerElements,
		context.activeTrigger.current
	)

	if (didClose) {
		context.activeTrigger.current = null
	}

	return didClose
}

function onResize(context) {
	const isVisible = isPopupVisible(context.popupElement)
	const hasActiveTrigger = Boolean(context.activeTrigger.current)
	const shouldReposition = isVisible && hasActiveTrigger

	if (!shouldReposition) return shouldReposition

	setPopupPosition(context.popupElement, context.activeTrigger.current)
	return shouldReposition
}

function isPopupVisible(popupElement) {
	return popupElement.classList.contains('is-visible')
}

function isClickOnPopup(popupElement, target) {
	return popupElement.contains(target)
}

function isClickOnTrigger(triggerElements, target) {
	return triggerElements.some((triggerElement) =>
		triggerElement.contains(target)
	)
}

function shouldCloseOnOutsideClick(popupElement, triggerElements, target) {
	const clickedOnPopup = isClickOnPopup(popupElement, target)
	const clickedOnTrigger = isClickOnTrigger(triggerElements, target)
	const shouldClose = !clickedOnPopup && !clickedOnTrigger
	return shouldClose
}

function clearSwitchTimeout(switchTimeout) {
	if (switchTimeout.current) {
		clearTimeout(switchTimeout.current)
		switchTimeout.current = null
	}
	const didClearTimeout = true
	return didClearTimeout
}

function schedulePopupSwitch({
	popupElement,
	popupContent,
	nextTrigger,
	activeTrigger,
	switchTimeout,
	switchDelayMs,
}) {
	switchTimeout.current = setTimeout(() => {
		activeTrigger.current = nextTrigger
		openPopup(popupElement, popupContent, nextTrigger)
		switchTimeout.current = null
	}, switchDelayMs)
	const didScheduleSwitch = true
	return didScheduleSwitch
}

function hasPopupConfig(config) {
	return Boolean(config)
}

function isOptionButton(target) {
	const isOption = target?.classList?.contains('settings-popup__option')
	return isOption
}

function getOptionPayload(target) {
	const optionType = target?.dataset?.optionType || ''
	const optionValue = target?.dataset?.optionValue || ''
	const optionUrl = target?.dataset?.optionUrl || ''
	return { optionType, optionValue, optionUrl }
}

function applyOptionSelection(optionType, optionValue, optionUrl) {
	if (optionType === 'theme') {
		setTheme(optionValue)
	}
	if (optionType === 'language' && optionUrl) {
		window.location.assign(optionUrl)
	}
	const didApplySelection =
		optionType === 'theme' || optionType === 'language'
	return didApplySelection
}

function buildOptionMarkup(option, triggerType) {
	const hasImage = Boolean(option.img)
	const imageMarkup = hasImage
		? `<img src="/wp-content/themes/oblivio/images/${option.img}" alt="" aria-hidden="true" />`
		: ''
	const urlAttribute = option.url ? ` data-option-url="${option.url}"` : ''
	const markup =
		`<li><button class="settings-popup__option" type="button" ` +
		`data-option-type="${triggerType}" ` +
		`data-option-value="${option.value}"${urlAttribute}>${imageMarkup}${option.label}</button></li>`
	return markup
}

function buildOptionsMarkup(config, triggerType) {
	const options = getConfigOptions(config)
	return options
		.map((option) => buildOptionMarkup(option, triggerType))
		.join('')
}

function getTriggerType(triggerElement) {
	const triggerType = triggerElement?.dataset?.settingsTrigger || ''
	return triggerType
}

function setPopupOptions(popupContent, optionsMarkup) {
	setHTML(popupContent, optionsMarkup)
	const didSetOptions = true
	return didSetOptions
}

function setPopupContent(popupContent, triggerType) {
	const config = getPopupConfig(triggerType)
	const hasConfig = hasPopupConfig(config)

	if (!hasConfig) return hasConfig

	const optionsMarkup = buildOptionsMarkup(config, triggerType)
	const didSetOptions = setPopupOptions(popupContent, optionsMarkup)
	const didSetContent = hasConfig && didSetOptions

	return didSetContent
}

function getPopupConfig(triggerType) {
	const config = popupConfigs[triggerType] || null
	return config
}

function getConfigOptions(config) {
	const options = config?.options || []
	return options
}

function getTriggerRect(triggerElement) {
	return triggerElement.getBoundingClientRect()
}

function getScrollOffsets() {
	const docElement = document.documentElement
	const scrollTop = window.scrollY || docElement.scrollTop || 0
	const scrollLeft = window.scrollX || docElement.scrollLeft || 0
	return { scrollTop, scrollLeft }
}

function getViewportBounds(edgePadding) {
	const { scrollLeft } = getScrollOffsets()
	const viewportWidth = document.documentElement.clientWidth
	const viewportLeft = scrollLeft + edgePadding
	const viewportRight = scrollLeft + viewportWidth - edgePadding
	return { viewportLeft, viewportRight }
}

function getPopupMinWidth(triggerRect, minWidth) {
	return Math.max(triggerRect.width, minWidth)
}

function getPopupHeight(popupElement) {
	const popupHeight = popupElement.offsetHeight
	return popupHeight
}

function getPopupTopAligned(triggerRect, scrollTop, offset) {
	const top = triggerRect.bottom + scrollTop + offset
	return top
}

function getPopupBottomAligned(triggerRect, scrollTop, offset, popupHeight) {
	const top = triggerRect.top + scrollTop - offset - popupHeight
	return top
}

function getLeftAligned(triggerRect, scrollLeft) {
	const leftAligned = triggerRect.left + scrollLeft
	return leftAligned
}

function getRightAligned(triggerRect, scrollLeft, popupWidth) {
	const rightAligned = triggerRect.right + scrollLeft - popupWidth
	return rightAligned
}

function getPopupWidth(popupElement) {
	const popupWidth = popupElement.offsetWidth
	return popupWidth
}

function clampLeftWithinViewport(viewportLeft, viewportRight, popupWidth) {
	return Math.max(viewportLeft, viewportRight - popupWidth)
}

function getPopupLeft({
	triggerRect,
	scrollLeft,
	popupWidth,
	viewportLeft,
	viewportRight,
}) {
	const leftAligned = getLeftAligned(triggerRect, scrollLeft)
	const rightAligned = getRightAligned(triggerRect, scrollLeft, popupWidth)
	const fitsLeft =
		leftAligned >= viewportLeft && leftAligned + popupWidth <= viewportRight
	const fitsRight =
		rightAligned >= viewportLeft &&
		rightAligned + popupWidth <= viewportRight
	let left = leftAligned
	const leftMin = viewportLeft
	const leftMax = Math.max(viewportRight - popupWidth, leftMin)

	if (!fitsLeft && fitsRight) {
		left = rightAligned
	} else if (!fitsLeft && !fitsRight) {
		left = clampLeftWithinViewport(viewportLeft, viewportRight, popupWidth)
	}

	return Math.min(Math.max(left, leftMin), leftMax)
}

function getPopupTop({
	triggerRect,
	scrollTop,
	popupHeight,
	viewportTop,
	viewportBottom,
}) {
	const topAligned = getPopupTopAligned(
		triggerRect,
		scrollTop,
		popupLayout.offset
	)
	const bottomAligned = getPopupBottomAligned(
		triggerRect,
		scrollTop,
		popupLayout.offset,
		popupHeight
	)
	const fitsBelow = topAligned + popupHeight <= viewportBottom
	const fitsAbove = bottomAligned >= viewportTop
	let top = topAligned

	if (!fitsBelow && fitsAbove) {
		top = bottomAligned
	} else if (!fitsBelow && !fitsAbove) {
		top = Math.max(viewportTop, viewportBottom - popupHeight)
	}

	return top
}

function applyPopupPosition(popupElement, top, left, minWidth) {
	popupElement.style.top = `${top}px`
	popupElement.style.left = `${left}px`
	popupElement.style.minWidth = `${minWidth}px`
	return true
}

function setPopupPosition(popupElement, triggerElement) {
	const triggerRect = getTriggerRect(triggerElement)
	const { scrollTop, scrollLeft } = getScrollOffsets()
	const { viewportLeft, viewportRight } = getViewportBounds(
		popupLayout.edgePadding
	)
	const minWidth = getPopupMinWidth(triggerRect, popupLayout.minWidth)

	popupElement.style.minWidth = `${minWidth}px`

	const popupHeight = getPopupHeight(popupElement)
	const viewportTop = scrollTop + popupLayout.edgePadding
	const viewportBottom =
		scrollTop + window.innerHeight - popupLayout.edgePadding
	const top = getPopupTop({
		triggerRect,
		scrollTop,
		popupHeight,
		viewportTop,
		viewportBottom,
	})
	const popupWidth = getPopupWidth(popupElement)
	const left = getPopupLeft({
		triggerRect,
		scrollLeft,
		popupWidth,
		viewportLeft,
		viewportRight,
	})

	return applyPopupPosition(popupElement, top, left, minWidth)
}

function openPopup(popupElement, popupContent, triggerElement) {
	const triggerType = getTriggerType(triggerElement)
	const didSetContent = setPopupContent(popupContent, triggerType)

	if (!didSetContent) return didSetContent

	const didUnsetInert = setPopupInert(popupElement, false)
	const didSetPosition = setPopupPosition(popupElement, triggerElement)
	const didSetVisibility = setPopupVisibility(popupElement, true)
	const didSetExpanded = setTriggerExpanded(triggerElement, true)
	const didSetSelected = setTriggerSelected(triggerElement, true)
	const didOpenPopup =
		didSetContent &&
		didUnsetInert &&
		didSetPosition &&
		didSetVisibility &&
		didSetExpanded &&
		didSetSelected

	return didOpenPopup
}

function closePopup(popupElement, activeTrigger) {
	const didHandleFocus = focusTriggerIfNeeded(popupElement, activeTrigger)
	const didSetVisibility = setPopupVisibility(popupElement, false)
	const didSetInert = setPopupInert(popupElement, true)
	let didResetTrigger = true

	if (activeTrigger) {
		const didSetExpanded = setTriggerExpanded(activeTrigger, false)
		const didSetSelected = setTriggerSelected(activeTrigger, false)
		didResetTrigger = didSetExpanded && didSetSelected
	}

	const didClosePopup =
		didHandleFocus || (didSetVisibility && didSetInert && didResetTrigger)
	return didClosePopup
}

function shouldToggleActiveTrigger(
	activeTrigger,
	triggerElement,
	popupElement
) {
	const isSameTrigger = activeTrigger.current === triggerElement
	const isVisible = isPopupVisible(popupElement)
	const shouldToggle = isSameTrigger && isVisible
	return shouldToggle
}

function shouldSwitchTrigger(activeTrigger, triggerElement) {
	const hasActiveTrigger = Boolean(activeTrigger.current)
	const isDifferentTrigger = activeTrigger.current !== triggerElement
	const shouldSwitch = hasActiveTrigger && isDifferentTrigger
	return shouldSwitch
}

function focusTriggerIfNeeded(popupElement, activeTrigger) {
	const { activeElement } = popupElement.ownerDocument
	const hasFocusedChild = popupElement.contains(activeElement)
	if (hasFocusedChild && activeTrigger) activeTrigger.focus()
	const didHandleFocus = hasFocusedChild
	return didHandleFocus
}

function setTriggerSelected(triggerElement, isSelected) {
	if (isSelected) {
		triggerElement.setAttribute('selected', '')
	} else {
		triggerElement.removeAttribute('selected')
	}
	const didSetSelected = true
	return didSetSelected
}

function setTriggerExpanded(triggerElement, isExpanded) {
	triggerElement.setAttribute('aria-expanded', String(isExpanded))
	const didSetExpanded = true
	return didSetExpanded
}

function setPopupVisibility(popupElement, isVisible) {
	popupElement.classList.toggle('is-visible', isVisible)
	popupElement.setAttribute('aria-hidden', String(!isVisible))
	const didSetVisibility = true
	return didSetVisibility
}

function setPopupInert(popupElement, isInert) {
	popupElement.inert = isInert
	const didSetInert = true
	return didSetInert
}
